import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { EyeOffIcon, EyeOnIcon, FaExclamation } from "../../components/Icons";
import { Tooltip } from "../../components/UI";
import { LAYER_STATES } from "../../redux/reducers/mapViewReducer";
import { ConfigContext } from "../../utils/ConfigContext";

const LAYER_LIST_STATES = Object.freeze({
  ...LAYER_STATES,
  PARTIALLY_LOADED: "partiallyLoaded",
});

const useLayerListItem = ({ layerListVisible, groupedLayers }) => {
  const [showSubLayers, setShowSubLayers] = useState(false);
  const [_count, setCount] = useState(0);
  const layerViews = useRef({});
  const countRef = useRef(_count);
  const subLayersListeners = useRef([]);
  const activeModule = useSelector((state) => state.activeModule);
  const view = useSelector((state) => state.mapViewReducer.view);
  const { config } = useContext(ConfigContext);
  const { t } = useTranslation("common");

  useEffect(() => {
    if (!layerListVisible) {
      setShowSubLayers(false);
    }
  }, [layerListVisible]);

  const incrementCount = useCallback(() => {
    const count = countRef.current + 1;
    countRef.current = count;
    setCount(count);
  }, []);

  const generateSubLayerViews = (lv, l, alias) => {
    if (l.type === "group") {
      const subLayerViews = {};
      lv.layerViews.forEach((subLv) => {
        const { layer } = subLv;
        if (!layer && layer.originalSubLayerId == null) return;
        const originalSubLayerId = layer.originalSubLayerId;
        subLayerViews[originalSubLayerId] = {
          id: originalSubLayerId,
          listener: null,
          isUpdating: false,
          isVisible: layer.visible,
          title: layer.title,
          layer,
        };

        const visibleHandler = reactiveUtils.watch(
          () => layer.visible,
          (visible) => {
            layerViews.current[alias].subLayerViews[
              originalSubLayerId
            ].isVisible = visible;
            incrementCount();
          }
        );
        subLayersListeners.current.push(visibleHandler);
      });

      return subLayerViews;
    }

    return null;
  };

  useEffect(() => {
    if (!view) return;

    const visibilityListeners = [];
    const layers = view.map.layers.filter((layer) => {
      return groupedLayers.some(
        (item) => layer.layerConfig && item.id === layer.layerConfig?.id
      );
    });

    const layerViewPromises = layers.map((layer) => view.whenLayerView(layer));

    Promise.allSettled(layerViewPromises)
      .then((results) => {
        results.forEach((result, index) => {
          if (result.status === "fulfilled") {
            const lv = result.value;
            const layer = lv.layer;
            if (!layer?.layerConfig) return;
            const alias = layer.layerConfig.alias;

            layerViews.current[alias] = {
              listener: null,
              isUpdating: false,
              isVisible: layer.visible,
              layer,
            };
            const subLayerViews = generateSubLayerViews(lv, layer, alias);

            if (subLayerViews) {
              layerViews.current[alias].subLayerViews = subLayerViews;
            }

            if (layer.visible) {
              incrementCount();
              layerViews.current[alias].listener = reactiveUtils.watch(
                () => lv.dataUpdating,
                (isUpdating) => {
                  layerViews.current[alias].isUpdating = isUpdating;
                  incrementCount();
                }
              );
            } else {
              if (layerViews.current[alias].listener) {
                layerViews.current[alias].listener.remove();
              }

              layerViews.current[alias].listener = null;
              layerViews.current[alias].isUpdating = false;

              incrementCount();
            }

            const visibleHandler = reactiveUtils.watch(
              () => layer.visible,
              (visible) => {
                layerViews.current[alias].isVisible = visible;
                if (layerViews.current[alias].subLayerViews) {
                  Object.values(
                    layerViews.current[alias].subLayerViews
                  ).forEach((slv) => {
                    slv.isVisible = visible;
                  });
                }
                view.map.layers.forEach((l) => {
                  if (layer.layerConfig.id === l.layerConfigId) {
                    l.visible = visible;
                  }
                });
                incrementCount();
              }
            );

            visibilityListeners.push(visibleHandler);
          } else {
            console.error(
              `Failed to load layer view for layer ${layers[index].id}:`,
              result.reason
            );
          }
        });
      })
      .catch((e) => {
        console.log(e);
      });

    return () => {
      visibilityListeners.forEach((l) => {
        l?.remove();
      });

      subLayersListeners.current.forEach((l) => {
        l?.remove();
      });

      Object.values(layerViews.current).forEach((lv) => {
        if (lv.listener) {
          lv.listener.remove();
        }
      });
    };
  }, [config, view, groupedLayers]);

  const showLoader = useMemo(() => {
    const isLoading =
      groupedLayers.filter((layer) => layer.state === LAYER_LIST_STATES.LOADING)
        .length > 0;

    const isUpdating =
      groupedLayers.filter((layer) => {
        return (
          layerViews.current[layer.alias]?.isUpdating ||
          layer.state === LAYER_LIST_STATES.LOADING
        );
      }).length > 0;

    return isUpdating || isLoading;
  }, [groupedLayers, _count]);

  const handleVisibilityChange = useCallback(
    (groupedLayers) => {
      const aliasesMap = new Set();
      groupedLayers.forEach((l) => {
        aliasesMap.add(l.alias);
      });

      const visibleLayers = new Set();
      view.map.layers.forEach((layer) => {
        const layerConfig = layer.layerConfig;
        if (layerConfig && aliasesMap.has(layerConfig.alias)) {
          if (layer.layerModules && layer.layerModules.includes(activeModule)) {
            const alias = layerConfig.alias;
            visibleLayers.add(alias);
            layer.visible = !layer.visible;
          }
        }
      });

      if (visibleLayers.size === 0) {
        view.map.layers.forEach((layer) => {
          const layerConfig = layer.layerConfig;

          if (layerConfig && aliasesMap.has(layerConfig.alias)) {
            layer.visible = !layer.visible;
          }
        });
      }
    },
    [activeModule, config]
  );

  const statusLayerIcon = useMemo(() => {
    const isVisible =
      groupedLayers.filter((layer) => {
        return layerViews.current[layer.alias]?.isVisible;
      }).length > 0;

    const loadedLayers = groupedLayers.filter((layer) => {
      return layer.state === LAYER_LIST_STATES.LOADED;
    });

    if (loadedLayers.length > 0) {
      return isVisible ? <EyeOnIcon stroke="#8a8a8a" /> : <EyeOffIcon />;
    }

    return null;
  }, [handleVisibilityChange, groupedLayers, _count]);

  const isVisible = useMemo(() => {
    let visible = true;

    if (Object.values(layerViews.current).length > 0) {
      visible =
        groupedLayers.filter((layer) => {
          return layerViews.current[layer.alias]?.isVisible;
        }).length > 0;
    }

    const loadedLayers = groupedLayers.filter((layer) => {
      return layer.state === LAYER_LIST_STATES.LOADED;
    });

    return visible;
  }, [groupedLayers, _count]);

  const subLayersViews = useMemo(() => {
    const subLayers = [];
    Object.values(layerViews.current).forEach((lv) => {
      if (lv.subLayerViews) {
        Object.values(lv.subLayerViews).forEach((subLv) => {
          subLayers.push(subLv);
        });
      }
    });

    return subLayers;
  }, [_count]);

  const layerConfig = useMemo(() => {
    if (groupedLayers.length > 0) {
      const groupLayer = groupedLayers.find((item) => !!item.layerConfig);
      return groupLayer?.layerConfig;
    }
  }, [groupedLayers]);

  const loadedLayersCount = useMemo(() => {
    return groupedLayers.filter(
      (layer) => layer.state === LAYER_LIST_STATES.LOADING
    ).length;
  }, [groupedLayers]);

  const errorLayersCount = useMemo(() => {
    return groupedLayers.filter(
      (layer) => layer.state === LAYER_LIST_STATES.ERROR
    ).length;
  }, [groupedLayers]);

  const renderErrorIcon = useCallback(
    (groupedLayers) => {
      if (errorLayersCount > 0) {
        return (
          <Tooltip
            showOnHover
            content={t("screen.widget.LayerList.errorTooltip", {
              defaultValue: "Layer didn't load correctly",
              1: errorLayersCount,
              2: groupedLayers.length,
            })}
            wrapperStyles={{
              display: "flex",
              width: 20,
              justifyContent: "center",
            }}
          >
            <FaExclamation fill="red" />
          </Tooltip>
        );
      }

      return null;
    },
    [errorLayersCount, loadedLayersCount]
  );

  return {
    showSubLayers,
    setShowSubLayers,
    incrementCount,
    layerViews: layerViews.current,
    subLayersViews,
    count: _count,
    isVisible,
    showLoader,
    handleVisibilityChange,
    statusLayerIcon,
    renderErrorIcon,
    layerConfig,
    loadedLayersCount,
    errorLayersCount,
  };
};

export default useLayerListItem;
